import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface TokenCostItem {
  date: string | null;
  phase: string | null;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read: number;
  cache_write: number;
  event_count: number;
}

interface TokenCostListResponse {
  items: TokenCostItem[];
  count: number;
  total: number;
}

export type GroupBy = "date" | "phase" | "model";

export interface TokenCostFilters {
  since?: string;
  until?: string;
  limit?: number;
}

export function useTokenCost(
  projectSlug: string | null,
  groupBy: GroupBy = "date",
  filters: TokenCostFilters = {},
  options: { enabled?: boolean } = {},
) {
  const isOrgMode = projectSlug === null;
  const queryKey = isOrgMode
    ? queryKeys.metrics.orgTokenCost(groupBy, filters)
    : queryKeys.metrics.tokenCost(projectSlug, groupBy, filters);

  const query = useQuery({
    queryKey,
    enabled: options.enabled !== false,
    queryFn: async () => {
      const params = new URLSearchParams({ group_by: groupBy });
      if (filters.since) params.set("since", filters.since);
      if (filters.until) params.set("until", filters.until);
      if (filters.limit !== undefined) params.set("limit", String(filters.limit));
      const url = isOrgMode
        ? `/api/v1/telemetry/token-cost?${params.toString()}`
        : `/api/v2/projects/${projectSlug}/memory/tokens?${params.toString()}`;
      const data = await api<TokenCostListResponse>(url);
      return data.items;
    },
  });

  // Original hook rethrew the underlying Error unchanged (only wrapping
  // non-Error rejections in a generic message) — api() always throws an
  // Error subclass, so this is effectively a message pass-through.
  return {
    items: query.data ?? [],
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Failed to load token data"
      : null,
  };
}
